import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  Stepper,
  Step,
  StepLabel,
  Grid,
  TextField,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  FormHelperText,
  CircularProgress,
  Pagination
} from '@mui/material';
import {
  Search as SearchIcon,
  KeyboardArrowRight as NextIcon,
  KeyboardArrowLeft as BackIcon,
  CheckCircle as CheckCircleIcon,
  RemoveCircle as RemoveCircleIcon,
  Add as AddIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import Layout from '../../components/layout/Layout';
import salesService from '../../services/salesService';

const ReturnOrdersPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [activeStep, setActiveStep] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [returnItems, setReturnItems] = useState([]);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  // Add pagination state
  const [pagination, setPagination] = useState({
    page: 0,
    totalPages: 0,
    totalElements: 0,
    size: 10
  });
  
  // Define returnReasons array
  const returnReasons = [
    'Product Damaged',
    'Defective Item',
    'Wrong Item Received',
    'Customer Dissatisfaction',
    'Exchange for Different Size/Color',
    'Other'
  ];

  // Fetch all orders from API with pagination and date filter
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      // Calculate date 15 days ago to limit returned orders
      const today = new Date();
      const fifteenDaysAgo = new Date();
      fifteenDaysAgo.setDate(today.getDate() - 15);
      
      // Format dates for API
      const startDate = fifteenDaysAgo.toISOString().split('T')[0];
      const endDate = today.toISOString().split('T')[0];
      
      // Get orders from last 15 days
      const dateRangeOrders = await salesService.getSalesOrdersByDateRange(startDate, endDate);
      
      // Show only DELIVERED orders and filter out RETURNED status
      const allOrders = dateRangeOrders.filter(order => 
        order.status === 'DELIVERED' && 
        order.status !== 'RETURNED'
      );
      
      // Apply pagination manually since the API doesn't support pagination for date range
      const startIndex = pagination.page * pagination.size;
      const endIndex = startIndex + pagination.size;
      const paginatedOrders = allOrders.slice(startIndex, endIndex);
      
      setOrders(allOrders);
      setFilteredOrders(paginatedOrders);
      
      setPagination({
        ...pagination,
        totalPages: Math.ceil(allOrders.length / pagination.size) || 1,
        totalElements: allOrders.length
      });
    } catch (error) {
      console.error('Error fetching orders:', error);
      enqueueSnackbar('Failed to load orders. Please try again.', { variant: 'error' });
      
      // Fallback to empty array
      setOrders([]);
      setFilteredOrders([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, enqueueSnackbar]);

  // Load orders on component mount
  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Update displayed orders when page changes
  useEffect(() => {
    if (orders.length > 0) {
      const startIndex = pagination.page * pagination.size;
      const endIndex = startIndex + pagination.size;
      setFilteredOrders(orders.slice(startIndex, endIndex));
    }
  }, [pagination.page, pagination.size, orders]);

  // Define handleSelectOrder with useCallback to prevent circular dependencies
  const handleSelectOrder = useCallback(async (order) => {
    setLoading(true);
    try {
      console.log(`Processing order selection for ID: ${order.id}`);
      
      // Fetch complete order details including items
      const orderDetails = await salesService.getSalesOrderById(order.id);
      console.log(`Fetched order details for order #${orderDetails.orderNumber}`);
      
      // Fetch previously returned quantities for this order
      console.log(`Fetching return history for order ID: ${order.id}`);
      const previouslyReturnedQty = await salesService.getPreviouslyReturnedQuantities(order.id);
      console.log(`Returned quantities history:`, previouslyReturnedQty);
      
      setSelectedOrder(orderDetails);
      
      // Initialize return items from order items, including previously returned quantities
      const items = (orderDetails.items || []).map(item => {
        const productId = item.product?.id || item.productId;
        console.log(`Processing product ID ${productId}: ${item.product?.name || 'Unknown'}`);
        
        const previouslyReturned = previouslyReturnedQty[productId] || 0;
        const availableQty = Math.max(0, item.quantity - previouslyReturned);
        
        console.log(`  - Ordered: ${item.quantity}, Previously returned: ${previouslyReturned}, Available: ${availableQty}`);
        
        return {
          ...item,
          previouslyReturned,
          availableQty,
          returnQuantity: 0,
          selected: false,
          productName: item.product?.name || 'Unknown Product'
        };
      });
      
      setReturnItems(items);
      setActiveStep(1);
    } catch (error) {
      console.error('Error fetching order details:', error);
      enqueueSnackbar('Failed to load order details. Please try again.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  // Check for orderId in URL search params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const orderId = params.get('orderId');
    
    if (orderId && orders.length > 0) {
      // Find the order in fetched data
      const order = orders.find(o => o.id.toString() === orderId);
      if (order) {
        handleSelectOrder(order);
      } else {
        // If not in the filtered list, try to fetch it directly
        setLoading(true);
        salesService.getSalesOrderById(orderId)
          .then(orderData => {
            if (orderData && orderData.status === 'DELIVERED') {
              handleSelectOrder(orderData);
            } else {
              enqueueSnackbar('Order not found or not eligible for return', { variant: 'error' });
              setLoading(false);
            }
          })
          .catch(error => {
            console.error('Error fetching order by ID:', error);
            enqueueSnackbar('Order not found or not eligible for return', { variant: 'error' });
            setLoading(false);
          });
      }
    }
  }, [location.search, orders, handleSelectOrder, enqueueSnackbar]);

  // Filter orders based on search query
  const handleSearch = () => {
    setLoading(true);
    
    setTimeout(() => {
      let filtered = orders;
      
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        filtered = orders.filter(
          order => 
            (order.orderNumber && order.orderNumber.toLowerCase().includes(query)) || 
            (order.customer?.name && order.customer.name.toLowerCase().includes(query)) ||
            (order.customerName && order.customerName.toLowerCase().includes(query))
        );
        
        if (filtered.length === 0) {
          enqueueSnackbar('No orders found with that search term', { variant: 'warning' });
        }
      }
      
      // Reset to first page when searching
      setPagination(prev => ({
        ...prev,
        page: 0,
        totalPages: Math.ceil(filtered.length / prev.size) || 1,
        totalElements: filtered.length
      }));
      
      // Apply pagination to filtered results
      const startIndex = 0; // Always start at first page for search results
      const endIndex = pagination.size;
      setFilteredOrders(filtered.slice(startIndex, endIndex));
      
      setLoading(false);
    }, 300);
  };

  // Handle page change for pagination
  const handlePageChange = (event, newPage) => {
    setPagination(prev => ({
      ...prev,
      page: newPage - 1 // MUI Pagination is 1-indexed, but our API is 0-indexed
    }));
  };

  const handleNext = () => {
    if (activeStep === 1) {
      const hasSelectedItems = returnItems.some(item => item.selected && item.returnQuantity > 0);
      if (!hasSelectedItems) {
        enqueueSnackbar('Please select at least one item to return', { variant: 'warning' });
        return;
      }
      if (!reason) {
        enqueueSnackbar('Please select a reason for return', { variant: 'warning' });
        return;
      }
    }
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleReturnQuantityChange = (itemId, value) => {
    setReturnItems(prev => 
      prev.map(item => {
        if (item.id === itemId) {
          console.log(`Changing return quantity for item ${itemId}:`, {
            productName: item.productName || item.product?.name,
            orderedQty: item.quantity,
            previouslyReturned: item.previouslyReturned,
            availableQty: item.availableQty,
            currentReturnQty: item.returnQuantity,
            requestedQty: value
          });
          
          // Ensure value is not greater than available quantity
          const newQty = Math.min(Math.max(0, value), item.availableQty);
          
          // If attempting to return more than available, show warning
          if (value > item.availableQty) {
            console.warn(`Attempted to return ${value} units but only ${item.availableQty} available`);
            enqueueSnackbar(
              `You can only return up to ${item.availableQty} units of this product. (${item.previouslyReturned} units were previously returned)`, 
              { variant: 'warning' }
            );
          }
          
          return { 
            ...item, 
            returnQuantity: newQty,
            selected: newQty > 0
          };
        }
        return item;
      })
    );
  };

  const handleToggleItem = (itemId) => {
    setReturnItems(prev => 
      prev.map(item => {
        if (item.id === itemId) {
          const newSelected = !item.selected;
          // Start with 1 when selected, but check if available
          const newQuantity = newSelected ? Math.min(1, item.availableQty) : 0;
          
          // Show warning if no available quantity
          if (newSelected && item.availableQty <= 0) {
            enqueueSnackbar('This item has no available quantity to return.', { variant: 'warning' });
          }
          
          return { 
            ...item, 
            selected: newSelected && item.availableQty > 0, // Only allow selection if quantity available
            returnQuantity: newQuantity
          };
        }
        return item;
      })
    );
  };

  // Calculate return totals with tax
  const getReturnTotals = () => {
    const subtotal = returnItems
      .filter(item => item.selected)
      .reduce((sum, item) => sum + (
        (item.unitPrice || item.product?.sellingPrice || 0) * item.returnQuantity
      ), 0);

    const taxRate = selectedOrder?.taxRate || 12; // Default to 12% if not specified
    const taxAmount = subtotal * (taxRate / 100);
    // The total should now be only the subtotal (no tax)
    const total = subtotal;

    return {
      subtotal,
      taxRate,
      taxAmount,
      total
    };
  };

  // Replace old getReturnTotal function
  const getReturnTotal = () => {
    const { total } = getReturnTotals();
    return total;
  };

  const handleProcessReturn = () => {
    setLoading(true);
    
    // Correctly use returnItems from state
    const selectedReturnItems = returnItems
      .filter(item => item.selected && item.returnQuantity > 0)
      .map(item => ({
        productId: item.product?.id || item.productId,
        quantity: item.returnQuantity,
        unitPrice: item.unitPrice || item.product?.sellingPrice || 0,
        total: (item.unitPrice || item.product?.sellingPrice || 0) * item.returnQuantity
      }));
    
    // Return data object
    const returnData = {
      items: selectedReturnItems,
      reason: reason,
      notes: notes,
      orderDate: new Date().toISOString().split('T')[0],
      total: getReturnTotal(),
      taxRate: selectedOrder?.taxRate || 12,
      taxAmount: getReturnTotals().taxAmount,
      subtotal: getReturnTotals().subtotal
    };
    
    // Make API call to process return
    salesService.processSalesReturn(selectedOrder.id, returnData)
      .then(response => {
        setLoading(false);
        enqueueSnackbar('Return processed successfully! Inventory has been updated.', { variant: 'success' });
        
        // Reset form
        setActiveStep(0);
        setSelectedOrder(null);
        setReturnItems([]);
        setReason('');
        setNotes('');
      })
      .catch(error => {
        setLoading(false);
        console.error('Error processing return:', error);
        
        let errorMessage = 'Failed to process return.';
        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }
        
        enqueueSnackbar(errorMessage, { variant: 'error' });
      });
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch (e) {
      return dateString;
    }
  };

  // Get customer name from order
  const getCustomerName = (order) => {
    if (!order) return '';
    
    if (order.customerName) return order.customerName;
    
    if (order.customer) {
      if (typeof order.customer === 'object') {
        return order.customer.name || `${order.customer.firstName || ''} ${order.customer.lastName || ''}`.trim() || 'Guest Customer';
      }
      return order.customer;
    }
    
    return 'Guest Customer';
  };

  // Steps for the return process
  const steps = ['Select Order', 'Select Items to Return', 'Confirm Return'];

  // Render step content based on active step
  const getStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <TextField
                  label="Search by Order Number or Customer"
                  variant="outlined"
                  fullWidth
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleSearch} edge="end">
                          <SearchIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                />
              </Box>

              <Typography variant="h6" gutterBottom>
                Select an Order to Process Return
              </Typography>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Order #</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                          <CircularProgress size={30} />
                        </TableCell>
                      </TableRow>
                    ) : filteredOrders.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                          No orders found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredOrders.map((order) => (
                        <TableRow key={order.id}>
                          <TableCell>{order.orderNumber || `Order #${order.id}`}</TableCell>
                          <TableCell>{getCustomerName(order)}</TableCell>
                          <TableCell>{formatDate(order.orderDate)}</TableCell>
                          <TableCell>
                            <Chip 
                              label={order.status} 
                              color={order.status === 'DELIVERED' ? 'success' : 'primary'} 
                              size="small" 
                            />
                          </TableCell>
                          <TableCell align="right">${(order.total || 0).toFixed(2)}</TableCell>
                          <TableCell align="right">
                            <Button 
                              variant="contained" 
                              size="small" 
                              onClick={() => handleSelectOrder(order)}
                            >
                              Select
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination 
                    count={pagination.totalPages} 
                    page={pagination.page + 1} 
                    onChange={handlePageChange}
                    color="primary" 
                    showFirstButton 
                    showLastButton
                  />
                </Box>
              )}
            </Paper>
          </Box>
        );
      
      case 1:
        return (
          <Box>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Order: {selectedOrder?.orderNumber || `Order #${selectedOrder?.id}`}
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2">
                    <strong>Customer:</strong> {getCustomerName(selectedOrder)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Order Date:</strong> {formatDate(selectedOrder?.orderDate)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2">
                    <strong>Status:</strong> {selectedOrder?.status}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Total:</strong> ${(selectedOrder?.total || 0).toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ mb: 3 }} />

              <Typography variant="h6" gutterBottom>
                Select Items to Return
              </Typography>

              <TableContainer sx={{ mb: 3 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">Select</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="right">Ordered Qty</TableCell>
                      <TableCell align="right">Previously Returned</TableCell>
                      <TableCell align="right">Available Qty</TableCell>
                      <TableCell align="right">Return Qty</TableCell>
                      <TableCell align="right">Return Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {returnItems.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={item.selected}
                            onChange={() => handleToggleItem(item.id)}
                          />
                        </TableCell>
                        <TableCell>{item.productName || item.product?.name || 'Unknown Product'}</TableCell>
                        <TableCell align="right">
                          ${(item.unitPrice || item.product?.sellingPrice || 0).toFixed(2)}
                        </TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">
                          {item.previouslyReturned > 0 ? item.previouslyReturned : '-'}
                        </TableCell>
                        <TableCell align="right">{item.availableQty}</TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                            <IconButton 
                              size="small"
                              disabled={!item.selected || item.returnQuantity <= 0}
                              onClick={() => handleReturnQuantityChange(item.id, item.returnQuantity - 1)}
                            >
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                            <TextField
                              size="small"
                              type="number"
                              InputProps={{ inputProps: { min: 0, max: item.availableQty } }}
                              value={item.returnQuantity}
                              onChange={(e) => handleReturnQuantityChange(
                                item.id, 
                                e.target.value === '' ? 0 : parseInt(e.target.value, 10)
                              )}
                              disabled={!item.selected}
                              sx={{ width: 60, mx: 1, '& input': { textAlign: 'center' } }}
                            />
                            <IconButton 
                              size="small"
                              disabled={!item.selected || item.returnQuantity >= item.availableQty}
                              onClick={() => handleReturnQuantityChange(item.id, item.returnQuantity + 1)}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          ${((item.unitPrice || item.product?.sellingPrice || 0) * item.returnQuantity).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required sx={{ mb: 2 }}>
                    <InputLabel id="return-reason-label">Reason for Return</InputLabel>
                    <Select
                      labelId="return-reason-label"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      label="Reason for Return"
                    >
                      {returnReasons.map((reasonOption) => (
                        <MenuItem key={reasonOption} value={reasonOption}>
                          {reasonOption}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    label="Additional Notes"
                    multiline
                    rows={4}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Return Summary
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography>Items to Return:</Typography>
                      <Typography>
                        {returnItems.filter(item => item.selected && item.returnQuantity > 0).length}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography>Total Quantities:</Typography>
                      <Typography>
                        {returnItems
                          .filter(item => item.selected)
                          .reduce((sum, item) => sum + item.returnQuantity, 0)}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography>Subtotal:</Typography>
                      <Typography>
                        ${getReturnTotals().subtotal.toFixed(2)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography>Return Total:</Typography>
                      <Typography variant="h6" color="primary">
                        ${getReturnTotal().toFixed(2)}
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        );
      
      case 2:
        return (
          <Box>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <CheckCircleIcon color="success" sx={{ fontSize: 40, mr: 2 }} />
                <Typography variant="h5">Confirm Return Details</Typography>
              </Box>

              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" gutterBottom>
                    <strong>Order Number:</strong> {selectedOrder?.orderNumber || `Order #${selectedOrder?.id}`}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <strong>Customer:</strong> {getCustomerName(selectedOrder)}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <strong>Order Date:</strong> {formatDate(selectedOrder?.orderDate)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" gutterBottom><strong>Return Reason:</strong> {reason}</Typography>
                  {notes && (
                    <Typography variant="body1" gutterBottom><strong>Notes:</strong> {notes}</Typography>
                  )}
                </Grid>
              </Grid>

              <Typography variant="h6" gutterBottom>Items to Return</Typography>
              <TableContainer sx={{ mb: 3 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">Ordered Qty</TableCell>
                      <TableCell align="right">Previously Returned</TableCell>
                      <TableCell align="right">Return Quantity</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="right">Return Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {returnItems
                      .filter(item => item.selected && item.returnQuantity > 0)
                      .map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>{item.productName || item.product?.name || 'Unknown Product'}</TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          <TableCell align="right">
                            {item.previouslyReturned > 0 ? item.previouslyReturned : '-'}
                          </TableCell>
                          <TableCell align="right">{item.returnQuantity}</TableCell>
                          <TableCell align="right">
                            ${(item.unitPrice || item.product?.sellingPrice || 0).toFixed(2)}
                          </TableCell>
                          <TableCell align="right">
                            ${((item.unitPrice || item.product?.sellingPrice || 0) * item.returnQuantity).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', mt: 2 }}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Subtotal: ${getReturnTotals().subtotal.toFixed(2)}
                </Typography>
                <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                  Total Return Amount: ${getReturnTotal().toFixed(2)}
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Typography variant="body2" color="textSecondary" gutterBottom>
                By confirming this return, you acknowledge that the returned items will be added back to your inventory.
              </Typography>
            </Paper>
          </Box>
        );

      default:
        return <Box>Unknown step</Box>;
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom component="h1">
          Process Sales Return
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {getStepContent()}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          {activeStep > 0 && (
            <Button
              onClick={handleBack}
              sx={{ mr: 1 }}
              startIcon={<BackIcon />}
            >
              Back
            </Button>
          )}
          
          {activeStep < steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={<NextIcon />}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={handleProcessReturn}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
            >
              {loading ? 'Processing...' : 'Process Return'}
            </Button>
          )}
        </Box>
      </Box>
    </Layout>
  );
};

export default ReturnOrdersPage; 